import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Image, Spin, Alert, Select, DatePicker } from "antd";
import { FaChartLine, FaShoppingCart, FaFilter } from "react-icons/fa";
import axios from "axios";
import { base_url as baseURL } from "../utils/baseUrl";

const { Option } = Select;
const { RangePicker } = DatePicker;

const TopSellingProducts = ({ filterPeriod = "monthly", dateRange = null }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    fetchTopSellingProducts();
  }, [filterPeriod, dateRange]);

  const fetchTopSellingProducts = async () => {
    try {
      setLoading(true);
      let url = `${baseURL}/api/sales/top-selling-products`;
      const params = new URLSearchParams();

      // Always send period parameter to ensure backend knows the filter
      params.append("period", filterPeriod);

      if (dateRange && dateRange.length === 2) {
        try {
          params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
          params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
        } catch (error) {
          console.error("Date formatting error:", error);
          // Fallback to manual date formatting
          const startDate = dateRange[0];
          const endDate = dateRange[1];
          params.append("startDate", `${startDate.$y}-${String(startDate.$M + 1).padStart(2, '0')}-${String(startDate.$D).padStart(2, '0')}`);
          params.append("endDate", `${endDate.$y}-${String(endDate.$M + 1).padStart(2, '0')}-${String(endDate.$D).padStart(2, '0')}`);
        }
      }

      // Add limit parameter - fixed at 5
      params.append("limit", 5);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      const newProducts = response.data.products || [];

      setProducts(newProducts);
      setError(null);
    } catch (err) {
      console.error("Error fetching top selling products:", err);
      if (err.response?.status === 404) {
        setError("Top selling products endpoint not found. Please ensure the backend server is running and the sales routes are deployed.");
      } else if (err.code === 'ERR_NETWORK') {
        setError("Unable to connect to the server. Please check your internet connection and try again.");
      } else {
        setError("Failed to fetch top selling products. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };



  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 60,
      render: (_, __, index) => (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          backgroundColor: index < 3 ? "#6B3F4F" : "#f0f0f0",
          color: index < 3 ? "white" : "#666",
          fontWeight: "bold"
        }}>
          {index + 1}
        </div>
      ),
    },
    {
      title: "Product Image",
      dataIndex: "product_image",
      key: "product_image",
      width: 80,
      render: (imageUrl) => (
        <Image
          width={50}
          height={50}
          src={imageUrl ? `${baseURL}/${imageUrl}` : "https://via.placeholder.com/50x50?text=No+Image"}
          fallback="https://via.placeholder.com/50x50?text=No+Image"
          style={{ objectFit: "cover", borderRadius: "4px" }}
        />
      ),
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      render: (name) => (
        <div style={{ fontWeight: "500", color: "#333" }}>
          {name}
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 100,
      render: (price, record) => (
        <div>
          <div style={{ fontWeight: "bold", color: "#6B3F4F" }}>
            ₹{price?.toLocaleString()}
          </div>
          {record.discount > 0 && (
            <div style={{ fontSize: "12px", color: "#52c41a" }}>
              {record.discount}% OFF
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Total Sold",
      dataIndex: "total_quantity_sold",
      key: "total_quantity_sold",
      width: 120,
      render: (quantity) => (
        <Tag color="green" icon={<FaShoppingCart />}>
          {quantity} units
        </Tag>
      ),
    },
    {
      title: "Total Orders",
      dataIndex: "total_orders",
      key: "total_orders",
      width: 120,
      render: (orders) => (
        <Tag color="blue">
          {orders} orders
        </Tag>
      ),
    },
    {
      title: "Stock",
      dataIndex: "stock_quantity",
      key: "stock_quantity",
      width: 100,
      render: (stock) => (
        <Tag color={stock > 10 ? "green" : stock > 0 ? "orange" : "red"}>
          {stock} in stock
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <Card title="Top Selling Products" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading top selling products...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Top Selling Products" style={{ marginBottom: 16 }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <button
              onClick={fetchTopSellingProducts}
              style={{
                background: "#6B3F4F",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Retry
            </button>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FaChartLine style={{ color: "#6B3F4F" }} />
          <span>Top 5 Selling Products</span>
        </div>
      }
      style={{ marginBottom: 16 }}

    >
      {products.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <FaShoppingCart style={{ fontSize: "48px", marginBottom: "16px" }} />
          <div>No products have been sold yet</div>
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={products}
            rowKey="product_id"
            pagination={false}
            size="small"
            style={{ marginTop: 16 }}
          />

        </>
      )}
    </Card>
  );
};

export default TopSellingProducts; 